import * as XLSX from 'xlsx';
import { Reservation, House } from '../services/types';
import { formatDate } from './reservationHelpers';

/**
 * Export reservations data to Excel file
 * @param reservations - Array of reservations to export
 * @param houses - Array of houses for name lookup
 * @param filename - Optional filename (defaults to "reservations_export_{date}.xlsx")
 */
export const exportReservationsToExcel = (
    reservations: Reservation[],
    houses: House[],
    filename?: string
): void => {
    // Helper function to get house name
    const getHouseName = (houseId: string): string => {
        if (!houseId) return 'No House';
        const house = houses.find(h => h.public_id === houseId);
        return house?.name || house?.address || 'Unknown House';
    };

    // Helper function to calculate nights
    const calculateNights = (checkIn: string, checkOut: string): number => {
        const start = new Date(checkIn);
        const end = new Date(checkOut);
        return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    };

    // Transform reservations data into Excel-friendly format
    const data = reservations.map((reservation) => ({
        'Reservation ID': reservation.public_id,
        'Status': reservation.reservation_state.charAt(0).toUpperCase() + reservation.reservation_state.slice(1),
        'Guest Name': reservation.name_first_guess || 'Guest',
        'Property': getHouseName(reservation.house_id),
        'Platform': reservation.platform || '-',
        'Platform ID': reservation.id_reservation_platform || '-',
        'Contact': reservation.contact,
        'Check-in Date': formatDate(reservation.check_in_date, 'MMM dd, yyyy'),
        'Check-out Date': formatDate(reservation.check_out_date, 'MMM dd, yyyy'),
        'Check-in Hour': reservation.check_in_hour || '-',
        'Check-out Hour': reservation.check_out_hour || '-',
        'Nights': calculateNights(reservation.check_in_date, reservation.check_out_date),
        'Number of Guests': reservation.number_of_guests || '-',
        'Rooms': reservation.rooms,
        'Nationality': reservation.nationality || '-',
        'Pets': reservation.pets ? 'Yes' : 'No',
        'Baby': reservation.baby ? 'Yes' : 'No',
        'Deposit Number': reservation.deposit_number || '-',
        'Is Paid': reservation.is_paid ? 'Yes' : 'No',
        'Observations': reservation.observations || '-',
        'Google Calendar Event ID': reservation.google_calendar_event_id || '-',
        'Created At': formatDate(reservation.created_at, 'MMM dd, yyyy HH:mm'),
        'Updated At': formatDate(reservation.updated_at, 'MMM dd, yyyy HH:mm'),
    }));

    // Create a new workbook
    const wb = XLSX.utils.book_new();

    // Convert data to worksheet
    const ws = XLSX.utils.json_to_sheet(data);

    // Set column widths for better readability
    const colWidths = [
        { wch: 30 }, // Reservation ID
        { wch: 12 }, // Status
        { wch: 20 }, // Guest Name
        { wch: 25 }, // Property
        { wch: 15 }, // Platform
        { wch: 20 }, // Platform ID
        { wch: 25 }, // Contact
        { wch: 15 }, // Check-in Date
        { wch: 15 }, // Check-out Date
        { wch: 12 }, // Check-in Hour
        { wch: 12 }, // Check-out Hour
        { wch: 8 },  // Nights
        { wch: 15 }, // Number of Guests
        { wch: 8 },  // Rooms
        { wch: 15 }, // Nationality
        { wch: 8 },  // Pets
        { wch: 8 },  // Baby
        { wch: 15 }, // Deposit Number
        { wch: 10 }, // Is Paid
        { wch: 30 }, // Observations
        { wch: 30 }, // Google Calendar Event ID
        { wch: 20 }, // Created At
        { wch: 20 }, // Updated At
    ];
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Reservations');

    // Generate filename with current date if not provided
    const defaultFilename = `reservations_export_${new Date().toISOString().split('T')[0]}.xlsx`;
    const finalFilename = filename || defaultFilename;

    // Write file
    XLSX.writeFile(wb, finalFilename);
};